import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import {
  NavigationDropdown,
  NavigationItem,
  NavigationLink,
} from "../../../interfaces/navigation-item.interface";
import { dropdownAnimation } from "../../../animations/dropdown.animation";
import { NavigationEnd, Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { filter } from "rxjs/operators";
import { NavigationService } from "../../../services/navigation.service";
import icKeyboardArrowRight from "@iconify/icons-ic/twotone-keyboard-arrow-right";
import { AuthService } from "src/app/pages/auth/authServices";
import { Subscription } from "rxjs";

@UntilDestroy()
@Component({
  selector: "vex-sidenav-item",
  templateUrl: "./sidenav-item.component.html",
  styleUrls: ["./sidenav-item.component.scss"],
  animations: [dropdownAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavItemComponent implements OnInit, OnChanges {
  @Input() item: NavigationItem;
  @Input() level: number;
  isOpen: boolean;
  isActive: boolean;
  isVisiable: boolean = true;
  isVisiable2: boolean = false;
  icKeyboardArrowRight = icKeyboardArrowRight;

  isLink = this.navigationService.isLink;
  isDropdown = this.navigationService.isDropdown;
  isSubheading = this.navigationService.isSubheading;
  userIsAuthenticated: boolean;
  private authStatusSub: Subscription;
  userId: string;
  constructor(
    private router: Router,
    private cd: ChangeDetectorRef,
    private navigationService: NavigationService,
    private authService: AuthService
  ) {}

  @HostBinding("class")
  get levelClass() {
    return `item-level-${this.level}`;
  }

  ngOnInit() {
    this.setupAuthentication()
    this.computeVisibility();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        untilDestroyed(this)
      )
      .subscribe(() => {
        if (this.isDropdown(this.item)) {
          this.onRouteChange();
        }
        this.cd.markForCheck();
      });

    this.navigationService.openChange$
      .pipe(
        filter(() => this.isDropdown(this.item)),
        untilDestroyed(this)
      )
      .subscribe((item) => this.onOpenChange(item));
  }

  private setupAuthentication() {
    this.userIsAuthenticated = this.authService.getIsAuth();
    this.authStatusSub = this.authService
      .getAuthStatusListener()
      .subscribe((isAuthenticated) => {
        this.userIsAuthenticated = isAuthenticated;
        this.userId = this.authService.getUserId();
        // Recompute visibility immediately and again shortly after,
        // since permissions may be refreshed asynchronously.
        this.computeVisibility();
        setTimeout(() => this.computeVisibility(), 300);
      });

    // Re-evaluate nav item visibility whenever the active role is switched.
    this.authService.roleChanged$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.computeVisibility());
  }
  

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes &&
      changes.hasOwnProperty("item") &&
      this.isDropdown(this.item)
    ) {
      this.onRouteChange();
    }
  }

  toggleOpen() {
    this.isOpen = !this.isOpen;
    this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
    this.cd.markForCheck();
  }

  onOpenChange(item: NavigationDropdown) {
    if (this.isChildrenOf(this.item as NavigationDropdown, item)) {
      return;
    }

    if (this.hasActiveChilds(this.item as NavigationDropdown)) {
      return;
    }

    if (this.item !== item) {
      this.isOpen = false;
      this.cd.markForCheck();
    }
  }

  onRouteChange() {
    if (this.hasActiveChilds(this.item as NavigationDropdown)) {
      this.isActive = true;
      this.isOpen = true;
      this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
      this.cd.markForCheck();
    } else {
      this.isActive = false;
      this.isOpen = false;
      this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
      this.cd.markForCheck();
    }
  }

  isChildrenOf(parent: NavigationDropdown, item: NavigationDropdown) {
    if (parent.children.indexOf(item) !== -1) {
      return true;
    }
    if (!parent) return true;

    return parent.children
      .filter((child) => this.isDropdown(child))
      .some((child) => this.isChildrenOf(child as NavigationDropdown, item));
  }

  hasActiveChilds(parent: NavigationDropdown) {
    if (!parent.children) return false;
    return parent.children.some((child) => {
      if (this.isDropdown(child)) {
        return this.hasActiveChilds(child);
      }

      if (this.isLink(child) && !this.isFunction(child.route)) {
        return this.router.isActive(child.route as string, false);
      }
    });
  }

  isFunction(prop: NavigationLink["route"]) {
    return prop instanceof Function;
  }

  isItemActive(item: NavigationItem): boolean {
    if (!item) return false;
    if (this.isLink(item) && !this.isFunction(item.route)) {
      const routeStr = item.route as string;
      return this.router.isActive(
        routeStr,
        item.routerLinkActiveOptions?.exact ?? false
      );
    }
    return false;
  }

  private computeVisibility() {
    try {
      const stored = localStorage.getItem("permission");
      const storedObj: any[] = stored ? JSON.parse(stored) : [];

      // Defaults
      this.isVisiable = false;
      this.isVisiable2 = false;

      // Main component (dropdown) visibility: show if any subComponent has a permitted permission
      for (const c of storedObj) {
        if (c.component === this.item.label) {
          const hasTruePermission = Array.isArray(c.subComponent) && c.subComponent.some((subComp: any) => {
            return Array.isArray(subComp.permissions) && subComp.permissions.some((permission: any) => permission?.isPermission === true);
          });
          this.isVisiable = !!hasTruePermission;
          break;
        }
      }

      // Link (subComponent) visibility
      for (const p of storedObj) {
        if (!Array.isArray(p.subComponent)) continue;
        for (const s of p.subComponent) {
          if (s.subComponentName === this.item.label) {
            if (this.item.label === "Digital Service") {
              this.isVisiable2 = p.component === "Database" && Array.isArray(s.permissions) && s.permissions.some((permission: any) => permission?.name === "View Digital Service" && permission?.isPermission === true);
              this.cd.markForCheck();
              return;
            }
            const hasTruePermission2 = Array.isArray(s.permissions) && s.permissions.some((permission: any) => permission?.isPermission === true);
            this.isVisiable2 = !!hasTruePermission2;
            this.cd.markForCheck();
            return;
          }

          // Special case for "View Encounters" under "Calendar"
          if (s.subComponentName === "Calendar" && this.item.label === "View Encounters") {
            const hasTruePermission2 = Array.isArray(s.permissions) && s.permissions.some(
              (permission: any) => permission?.name === "View Encounters" && permission?.isPermission === true
            );
            this.isVisiable2 = !!hasTruePermission2;
            this.cd.markForCheck();
            return;
          }

          // UI label "Report Print Queue" maps to RBAC key "USPS Print Queue"
          if (
            s.subComponentName === "USPS Print Queue" &&
            this.item.label === "Report Print Queue"
          ) {
            const hasTruePermission2 =
              Array.isArray(s.permissions) &&
              s.permissions.some(
                (permission: any) => permission?.isPermission === true
              );
            this.isVisiable2 = !!hasTruePermission2;
            this.cd.markForCheck();
            return;
          }
        }
      }

      this.cd.markForCheck();
    } catch (e) {
      // Fallback: hide item on parse errors
      this.isVisiable = false;
      this.isVisiable2 = false;
      this.cd.markForCheck();
    }
  }
}
